import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  SmartToy as SmartToyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import api from '../api/axios';

const CurrentAssistantInfo = ({ qualificationId, step, onEditAssistant }) => {
  const [assistant, setAssistant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!qualificationId || !step) return;

    setLoading(true);
    setError('');
    
    api.get(`/qualifications/${qualificationId}/assistant/${step}`)
      .then(res => {
        if (res.data?.openaiUnavailable) {
          setAssistant(res.data);
          setError('');
          return;
        }
        setAssistant(res.data);
      })
      .catch(err => {
        setError('Failed to load assistant information');

      })
      .finally(() => {
        setLoading(false);
      });
  }, [qualificationId, step]);

  if (loading) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading assistant information...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  if (!assistant) {
    return null;
  }

  if (assistant.openaiUnavailable) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Alert severity="warning">
            {assistant.system_instruction || 'OpenAI assistant is not available on this server.'}
            {assistant.installHint && (
              <Typography variant="caption" component="div" sx={{ mt: 1, fontFamily: 'monospace' }}>
                Server fix: {assistant.installHint}
              </Typography>
            )}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <SmartToyIcon color="primary" />
            <Typography variant="h6">AI Assistant</Typography>
            <Chip 
              label={assistant.name || `Step ${step}`} 
              size="small" 
              color="primary" 
              variant="outlined"
            />
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Edit Assistant">
              <IconButton 
                size="small" 
                onClick={() => onEditAssistant(assistant)}
                color="primary"
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={expanded ? "Show less" : "Show more"}>
              <IconButton 
                size="small" 
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box mb={2}>
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="textSecondary" component="div" gutterBottom>
              Model: <Chip label={assistant.model || 'gpt-4o-mini'} size="small" sx={{ ml: 0.5 }} />
            </Typography>
          </Box>
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="textSecondary" component="div" gutterBottom>
              Assistant ID: <code>{assistant.openai_assistant_id}</code>
            </Typography>
          </Box>
          {assistant.file_ids && assistant.file_ids.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" color="textSecondary" component="div" gutterBottom>
                Files: <Chip label={`${assistant.file_ids.length} file(s)`} size="small" sx={{ ml: 0.5 }} />
              </Typography>
            </Box>
          )}
        </Box>

        <Collapse in={expanded}>
          <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>
              Instructions:
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                backgroundColor: '#f5f5f5', 
                p: 1, 
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                whiteSpace: 'pre-wrap'
              }}
            >
              {assistant.instructions || assistant.system_instruction || 'No instructions available'}
            </Typography>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default CurrentAssistantInfo; 